import { useState } from 'react';
import type { AdminQuotaState } from '@chefty/shared-types';
import { adminApi } from '../services/admin';
import { Eyebrow, Panel } from './ui';
import { fmtInt, fmtDateTime } from '../lib/format';

const MAX_EXTRA = 1000;

/**
 * Admin controls for a user's free-tier import allowance. Holds its own copy of
 * the quota state (seeded from the user detail) so a Save/Reset updates in place
 * from the mutation's response — no full page refetch. The allowance applies
 * whenever the user is on the free tier; it's simply not enforced while they hold
 * an active Plus plan.
 */
export function QuotaCard({ userId, initial }: { userId: string; initial: AdminQuotaState }) {
  const [state, setState] = useState<AdminQuotaState>(initial);
  const [extra, setExtra] = useState(String(initial.importQuotaExtra));
  const [busy, setBusy] = useState<null | 'save' | 'reset'>(null);
  const [error, setError] = useState<string | null>(null);

  const parsedExtra = Number(extra);
  const extraValid = Number.isInteger(parsedExtra) && parsedExtra >= 0 && parsedExtra <= MAX_EXTRA;
  const dirty = parsedExtra !== state.importQuotaExtra;

  async function run(kind: 'save' | 'reset', op: () => Promise<AdminQuotaState>) {
    setBusy(kind);
    setError(null);
    try {
      const next = await op();
      setState(next);
      setExtra(String(next.importQuotaExtra));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setBusy(null);
    }
  }

  const used = `${fmtInt(state.importsUsed)} / ${fmtInt(state.weeklyLimit)}`;

  return (
    <Panel className="rise">
      <div className="flex items-center justify-between">
        <Eyebrow>Import quota</Eyebrow>
        <span className="tnum text-sm text-cream-300">{used} this week</span>
      </div>

      <div className="mt-4 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs text-cream-400">Extra weekly imports</span>
            <input
              type="number"
              min={0}
              max={MAX_EXTRA}
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              className="w-32 rounded-xl border border-cream-600/20 bg-ink-900/70 px-3 py-2 text-sm text-cream-100 outline-none transition-colors focus:border-saffron-500/50 focus:bg-ink-850"
            />
          </label>
          <button
            onClick={() => run('save', () => adminApi.setQuota(userId, parsedExtra))}
            disabled={!extraValid || !dirty || busy !== null}
            className="rounded-lg border border-saffron-500/50 bg-saffron-500/10 px-3 py-2 text-xs font-medium text-saffron-300 transition-colors hover:bg-saffron-500/20 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy === 'save' ? 'Saving…' : 'Save allowance'}
          </button>
          <button
            onClick={() => {
              if (window.confirm('Reset this user’s import quota? They get a fresh window now.')) {
                void run('reset', () => adminApi.resetQuota(userId));
              }
            }}
            disabled={busy !== null}
            className="rounded-lg border border-cream-600/15 px-3 py-2 text-xs font-medium text-cream-300 transition-colors hover:border-cream-600/30 hover:text-cream-100 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy === 'reset' ? 'Resetting…' : 'Reset quota'}
          </button>
        </div>

        <p className="text-xs text-cream-500">
          Effective limit = 5 base + {fmtInt(state.importQuotaExtra)} extra ={' '}
          <span className="text-cream-300">{fmtInt(state.weeklyLimit)}</span> per rolling week.{' '}
          {state.importQuotaResetAt
            ? `Last reset ${fmtDateTime(state.importQuotaResetAt)}.`
            : 'Never reset.'}
        </p>

        {!extraValid && <p className="text-xs text-bad">Enter a whole number from 0 to {MAX_EXTRA}.</p>}
        {error && <p className="text-xs text-bad">{error}</p>}
      </div>
    </Panel>
  );
}
